import * as React from 'react'
import { ExternalLink, FileText, Trash2, Upload } from 'lucide-react'
import {
  useCreateEventDocument,
  useDeleteEventDocument,
} from '@/hooks/useEvents'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { EVENT_DOCUMENT_KINDS, type EventDocumentKind } from '@lib/enums/event-document-kind.enum'
import {
  EVENT_DOCUMENT_UPLOAD_ACCEPT,
  EVENT_DOCUMENT_UPLOAD_MAX_BYTES,
  createEventDocumentInputSchema,
  eventDocumentDataUrlMime,
  eventDocumentInputMessage,
  isEventDocumentUploadMime,
} from '@lib/input/event-document.input'
import type { EventDetailDto } from '@lib/dto/event.dto'
import type { EventDocumentDto } from '@lib/dto/event-document.dto'

/** Extension to mime, for a file the browser did not type. */
const EXTENSION_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  txt: 'text/plain',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

const UNSUPPORTED_FILE = 'That file type can’t be uploaded. Use a PDF, image, text file, or Word or Excel document.'

function mimeForFile(file: File): string | null {
  const stated = file.type.trim().toLowerCase()
  if (isEventDocumentUploadMime(stated)) return stated
  const dot = file.name.lastIndexOf('.')
  if (dot < 0) return null
  return EXTENSION_MIME[file.name.slice(dot + 1).toLowerCase()] ?? null
}

/**
 * The data URL the API will store, named with `mime`.
 *
 * A browser that does not know the file leaves the prefix empty or
 * `application/octet-stream`. That prefix is rewritten from the extension.
 * A prefix that names some other type is refused — it is not relabeled.
 */
function alignDataUrlMime(dataUrl: string, mime: string): string | null {
  const match = /^data:([^,]*),([\s\S]*)$/.exec(dataUrl)
  if (!match) return null
  const header = match[1] ?? ''
  const body = match[2] ?? ''
  if (!header.endsWith(';base64')) return null
  const stated = header.slice(0, -';base64'.length).toLowerCase()
  if (stated === mime) return dataUrl
  if (stated !== '' && stated !== 'application/octet-stream') return null
  return `data:${mime};base64,${body}`
}

const KIND_LABELS: Record<EventDocumentKind, string> = {
  beo: 'BEO',
  contract: 'Contract',
  proposal: 'Proposal',
  floorplan: 'Floor plan',
  timeline: 'Timeline',
  other: 'Other',
}

function parseKind(value: string): EventDocumentKind {
  for (const k of EVENT_DOCUMENT_KINDS) {
    if (k === value) return k
  }
  return 'other'
}

function formatBytes(n: number | null): string {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function DocRow({
  eventId,
  doc,
  canRemove,
}: {
  eventId: string
  doc: EventDocumentDto
  canRemove: boolean
}) {
  const del = useDeleteEventDocument(eventId)
  const [err, setErr] = React.useState<string | null>(null)

  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{doc.name}</p>
        <p className="truncate text-xs text-ink-muted">
          {KIND_LABELS[doc.kind]}
          {doc.sizeBytes != null ? ` · ${formatBytes(doc.sizeBytes)}` : ''}
          {doc.externalKey ? ' · imported' : ''}
          {doc.addedBy ? ` · added by ${doc.addedBy}` : ''}
        </p>
        {err ? <FormError size="sm">{err}</FormError> : null}
      </div>
      <Button variant="ghost" size="sm" asChild>
        <a href={doc.url} target="_blank" rel="noreferrer">
          <ExternalLink className="h-4 w-4" /> Open
        </a>
      </Button>
      {canRemove ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-danger-ink hover:text-danger-ink"
          aria-label={`Delete ${doc.name}`}
          onClick={() =>
            del.mutate(
              { documentId: doc.id },
              { onError: (e) => setErr(requestErrorMessage(e)), onSuccess: () => setErr(null) },
            )
          }
          pending={del.isPending}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </li>
  )
}

/**
 * Event document store: list BEOs/contracts/etc., link an external URL, or
 * upload a file (when object storage is configured on the API).
 *
 * A planner adds documents but cannot remove them (docs/planner-vendors.md):
 * removing is left to the venue. Each says who added it, where that is known.
 * Staff whose role only views events open them, and neither add nor remove.
 */
export function EventDocumentsPanel({
  event,
  canEdit,
  canRemove = true,
}: {
  event: EventDetailDto
  /** Whether the viewer may add documents (and, with `canRemove`, remove them): staff who can edit events, or the planner. */
  canEdit: boolean
  canRemove?: boolean
}) {
  const create = useCreateEventDocument(event.id)
  const [name, setName] = React.useState('')
  const [kind, setKind] = React.useState<EventDocumentKind>('other')
  const [url, setUrl] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  // From choosing a file until its upload settles. The file is read before the
  // request starts, and a second choice in that gap read and sent another one.
  const uploading = React.useRef(false)
  const [readingFile, setReadingFile] = React.useState(false)
  const fileRef = React.useRef<HTMLInputElement>(null)

  const documents = event.documents

  function resetForm(): void {
    setName('')
    setKind('other')
    setUrl('')
    if (fileRef.current) fileRef.current.value = ''
  }

  function addByUrl(): void {
    setError(null)
    const trimmedName = name.trim()
    const trimmedUrl = url.trim()
    if (!trimmedName || !trimmedUrl) {
      setError('Name and URL are required.')
      return
    }
    // Infer content type from extension when possible. A link the API never
    // fetches may still be an unknown type; the schema only requires http(s).
    const lower = trimmedUrl.toLowerCase()
    let contentType = 'application/octet-stream'
    if (lower.endsWith('.pdf')) contentType = 'application/pdf'
    else if (lower.endsWith('.png')) contentType = 'image/png'
    else if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) contentType = 'image/jpeg'
    else if (lower.endsWith('.webp')) contentType = 'image/webp'

    const parsed = createEventDocumentInputSchema.safeParse({
      name: trimmedName,
      kind,
      url: trimmedUrl,
      contentType,
    })
    if (!parsed.success) {
      setError(eventDocumentInputMessage(parsed.error))
      return
    }
    create.mutate(parsed.data, {
      onSuccess: () => resetForm(),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  function onFile(file: File | null): void {
    if (!file || uploading.current) return
    setError(null)
    if (file.size > EVENT_DOCUMENT_UPLOAD_MAX_BYTES) {
      setError('That file is too large to upload. Link to it instead.')
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    const mime = mimeForFile(file)
    if (mime === null || !isEventDocumentUploadMime(mime)) {
      setError(UNSUPPORTED_FILE)
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    uploading.current = true
    setReadingFile(true)
    const settle = (): void => {
      uploading.current = false
      setReadingFile(false)
    }
    const reader = new FileReader()
    reader.onload = () => {
      const raw = typeof reader.result === 'string' ? reader.result : null
      const data = raw === null ? null : alignDataUrlMime(raw, mime)
      if (data === null || eventDocumentDataUrlMime(data) !== mime) {
        settle()
        setError(UNSUPPORTED_FILE)
        if (fileRef.current) fileRef.current.value = ''
        return
      }
      const parsed = createEventDocumentInputSchema.safeParse({
        name: name.trim() || file.name,
        kind,
        data,
        contentType: mime,
        sizeBytes: file.size,
      })
      if (!parsed.success) {
        settle()
        setError(eventDocumentInputMessage(parsed.error))
        if (fileRef.current) fileRef.current.value = ''
        return
      }
      create.mutate(parsed.data, {
        onSuccess: () => resetForm(),
        onError: (e) => setError(requestErrorMessage(e)),
        onSettled: settle,
      })
    }
    reader.onerror = () => {
      settle()
      setError('Could not read that file.')
    }
    reader.readAsDataURL(file)
  }

  return (
    <div className="space-y-4">
      {documents.length === 0 ? (
        <p className="text-sm text-ink-muted">No documents yet.{canEdit ? ' Link a URL or upload a file.' : null}</p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
          {documents.map((d) => (
            <DocRow key={d.id} eventId={event.id} doc={d} canRemove={canEdit && canRemove} />
          ))}
        </ul>
      )}

      {canEdit ? (
        <Card className="grid gap-3 p-4 sm:grid-cols-2">
          <Field label="Name" htmlFor="doc-name">
            <Input
              id="doc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Wedding BEO.pdf"
            />
          </Field>
          <Field label="Kind" htmlFor="doc-kind">
            <Select
              id="doc-kind"
              value={kind}
              onChange={(e) => setKind(parseKind(e.target.value))}
            >
              {EVENT_DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Link URL" htmlFor="doc-url" className="sm:col-span-2">
            <Input
              id="doc-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
            />
          </Field>
          <div className="flex flex-wrap items-center gap-2 sm:col-span-2">
            <Button pending={create.isPending} type="button" onClick={addByUrl}>
              Add link
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={create.isPending || readingFile}
              onClick={() => fileRef.current?.click()}
            >
              <Upload className="h-4 w-4" /> Upload file
            </Button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept={EVENT_DOCUMENT_UPLOAD_ACCEPT}
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
          </div>
          {error ? <FormError className="sm:col-span-2">{error}</FormError> : null}
        </Card>
      ) : null}
    </div>
  )
}
